import { useRef, useState, type ChangeEvent } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import * as yup from 'yup';
import { connect, type ConnectedProps } from 'react-redux';
import { isNonNullish, pick } from 'remeda';
import CustomTextField from '@/components/CustomTextField';
import Form, { type FormElement } from '@/components/Form';
import { type CompetitionWithMatches } from '@/slices/matches/types';
import { editCompetition } from '@/slices/matches/actions';
import { useYupValidationResolver } from '@/utils';
import { ModalLayout } from '../../layouts';
import styles from './styles';

type EditCompetitionForm = {
  name: string;
  category_name: string;
  ordinal: number | null;
};

const validationSchema = yup.object().shape({
  name: yup.string().required('Συμπληρώστε το όνομα διοργάνωσης'),
  category_name: yup.string().required('Συμπληρώστε το όνομα της χώρας'),
  ordinal: yup
    .number()
    .typeError('Η σειρά εμφάνισης πρέπει να είναι αριθμός')
    .nullable(),
});

type Props = ConnectedProps<typeof connector> & {
  closeModal: () => void;
  modalData: {
    competition: CompetitionWithMatches;
  };
};

const EditCompetitionModal = ({
  closeModal,
  modalData: { competition },
  editCompetition,
}: Props) => {
  const formRef = useRef<FormElement>(null);
  const resolver = useYupValidationResolver(validationSchema);
  const [syncDataChecked, setSyncDataChecked] = useState(false);

  const defaultValues: EditCompetitionForm = {
    name: competition?.name || '',
    category_name: competition?.category_name || '',
    ordinal: isNonNullish(competition?.ordinal)
      ? competition.ordinal + 1
      : null,
  };

  const handleDiscard = () => {
    closeModal();
  };

  const handleAccept = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const onSubmit = (formData: EditCompetitionForm) => {
    const { name, category_name, ordinal } = pick(formData, [
      'name',
      'category_name',
      'ordinal',
    ]);

    closeModal();

    editCompetition({
      external_id: competition.external_id,
      name,
      category_name,
      ordinal: isNonNullish(ordinal) ? ordinal - 1 : null,
      syncData: syncDataChecked,
    });
  };

  return (
    <ModalLayout
      closeModal={closeModal}
      title="Επεξεργασία στοιχείων"
      onAccept={handleAccept}
      acceptText="Αποθήκευση"
      onCancel={handleDiscard}
      cancelText="Απόρριψη"
    >
      <Box sx={styles.alert}>
        Αν επεξεργαστείτε τα στοιχεία, θα διακοπεί η διασύνδεση με το
        Sportradar. Εάν το επιθυμείτε, μπορείτε να επαναφέρετε τον συγχρονισμό
        αργότερα.
      </Box>

      <Box>
        <Box sx={styles.editCardHeader}>
          <Typography sx={styles.editCardHeaderText} typography="body2">
            {competition?.name}
          </Typography>
        </Box>

        <Form
          sx={styles.editCardBody}
          resolver={resolver}
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          ref={formRef}
        >
          <CustomTextField
            name="ordinal"
            label="Σειρά εμφάνισης"
            placeholder="Επιλογή"
          />

          <CustomTextField
            name="category_name"
            label="Χώρα"
            placeholder="Επιλογή"
          />

          <CustomTextField
            name="name"
            label="Διοργάνωση"
            placeholder="Επιλογή"
          />
        </Form>
      </Box>

      <Box sx={styles.sync}>
        <Checkbox
          sx={styles.checkcolor}
          checked={syncDataChecked}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setSyncDataChecked(e.target.checked)
          }
        />

        <Typography>Sync data ξανά</Typography>
      </Box>
    </ModalLayout>
  );
};

const connector = connect(null, {
  editCompetition,
});

export default connector(EditCompetitionModal);
